import { Fragment, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Pencil,
  PhoneCall,
  Repeat,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { TRIP_EDIT_MIN_LEAD_MS, type TripScheduleItem } from "@mtform/shared";
import { ApiError, api } from "@/lib/api";
import { useHasPermission } from "@/auth/usePermissions";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { ServiceIcon } from "@/components/icons/ServiceIcon";
import { formatServiceType, formatUsPhone, titleCaseName } from "@/lib/format";
import { prettyStatus, statusBadgeClasses } from "@/lib/trip-status";
import { cn } from "@/lib/utils";
import type { TripPairing } from "./BookedTripsView";
import { CancelTripDialog } from "./CancelTripDialog";
import { EditTripDialog, type EditTripPayload } from "./EditTripDialog";
import { highlightMatch } from "./highlight";

/** Statuses where the cancel-trip button should be offered. Will-call
 *  trips can be cancelled too (the patient never called in, plans
 *  changed, etc.) — they get *both* Activate Will Call and Cancel.
 *  Anything in flight (`en_route`, `arrived`, `in_progress`,
 *  `at_destination`) or already terminal (`finished`, any
 *  `*_cancel*`, `deleted`) hides the button — those trips can't be
 *  cancelled by dispatch through the portal anyway. */
const CANCELLABLE_STATUSES = new Set(["pending", "assigned", "will_call"]);

/** Statuses where the edit-trip button is offered. Will-call trips
 *  are editable too — the dialog hides the time row for those (a
 *  will-call has no fixed pickup time to edit), and only the pickup
 *  / destination address fields are exposed. The lead-time gate is a
 *  separate check; see `isEditableNow`. */
const EDITABLE_STATUSES = new Set(["pending", "assigned", "will_call"]);

/** Show the Edit button only when pickup is at least TRIP_EDIT_MIN_LEAD_HOURS
 *  out. The threshold is shared (see @mtform/shared/leadTime) with the dialog's
 *  save gate, the admin Bookings list, and the Permissions page copy, so the
 *  four cannot drift.
 *
 *  This is the ONLY thing standing between a user and a late edit — contrary to
 *  what this comment claimed before Aug 2026, the API does not re-validate it.
 *  A stale render therefore CAN send a late edit; upstream's own behaviour is
 *  unconfirmed. */
function isEditableNow(status: string, pickupAtIso: string | undefined): boolean {
  if (!EDITABLE_STATUSES.has(status)) return false;
  if (!pickupAtIso) return false;
  const t = Date.parse(pickupAtIso);
  if (!Number.isFinite(t)) return false;
  return t - Date.now() > TRIP_EDIT_MIN_LEAD_MS;
}

interface TripCardProps {
  trip: TripScheduleItem;
  query: string;
  /** The Detailed / Compact view-mode toggle. It now sets the row's DEFAULT
   *  open state (Detailed → expanded, Compact → collapsed); every row can
   *  still be expanded/collapsed individually by clicking it. */
  compact: boolean;
  dimmed: boolean;
  /** Set when this trip is one leg of a round trip (computed in
   *  BookedTripsView by grouping friendlyIds on their base prefix).
   *  Null for one-way trips. */
  pairing?: TripPairing | null;
  /** Status-group colour for the thin bar on the card's left edge
   *  (BookedTripsView passes its section's tone). */
  accent?: "emerald" | "sky" | "slate" | "rose";
}

const ACCENT_BAR: Record<NonNullable<TripCardProps["accent"]>, string> = {
  emerald: "bg-emerald-500/80 dark:bg-emerald-400/80",
  sky: "bg-sky-500/80 dark:bg-sky-400/80",
  slate: "bg-slate-400/70",
  rose: "bg-rose-500/70 dark:bg-rose-400/70",
};

// =============================================================================
// TripCard
//
// A single COMPACT, clickable row that EXPANDS to reveal the full trip
// detail — mirroring the Admin bookings list (AdminBookingsPage). The
// collapsed row is the at-a-glance summary (trip ID, patient · MRN, pickup
// time, status, quick-action icons); clicking it (or pressing Enter/Space
// while it's focused) toggles a detail pane with DOB, phones, addresses,
// cost center, and the note.
//
// The Detailed / Compact toggle sets the DEFAULT open state for the rows;
// `useEffect` re-applies it when the mode flips so a list of expanded rows
// collapses (and vice-versa) in one tap.
// =============================================================================

export function TripCard({
  trip,
  query,
  compact,
  dimmed,
  pairing,
  accent,
}: TripCardProps) {
  const status = String(trip.status ?? "").toLowerCase();
  const isWillCall = status === "will_call";
  const friendlyId = String(trip.friendlyId ?? "");
  const patientName = titleCaseName(trip.patientName);
  const etaCountdown = status === "en_route" ? formatEtaCountdown(trip.eta) : "";
  const svc = formatServiceType(trip.serviceType);

  // Detailed → expanded by default, Compact → collapsed. The effect re-applies
  // the default whenever the global mode flips, so toggling Detailed/Compact
  // opens/closes every row in one go while still allowing per-row toggling.
  const [expanded, setExpanded] = useState(!compact);
  // The detail pane unrolls only when the user opened it (a click, or the
  // Detailed/Compact switch). A card that mounts already open — Detailed
  // view, a trip arriving on the minute refresh, a search bringing it back —
  // just appears open.
  const [animateOpen, setAnimateOpen] = useState(false);
  const lastCompact = useRef(compact);
  useEffect(() => {
    if (lastCompact.current === compact) return;
    lastCompact.current = compact;
    setAnimateOpen(true);
    setExpanded(!compact);
  }, [compact]);

  const pairingTitle = pairing
    ? `Round trip · ${pairing.siblings
        .map((s) => `${s.label}: ${s.friendlyId} (${prettyStatus(s.status)})`)
        .join(", ")}`
    : "";

  const hasActions =
    isWillCall ||
    isEditableNow(status, trip.pickupAtIso) ||
    CANCELLABLE_STATUSES.has(status);

  return (
    <article
      // No entrance of its own: the list animates user switches, and a card
      // that appears on the minute refresh or while searching just appears
      // (open in Detailed view, without unrolling — see animateOpen).
      // Completed/Canceled read as quieter through the card's background and
      // softer name/time text, not by fading the whole card (60% opacity took
      // their text below 4.5:1).
      className={cn(
        "relative overflow-hidden border border-border/70 bg-card/70 text-left shadow-[0_18px_40px_-34px_hsl(222_90%_2%/0.9)] transition hover:border-foreground/20 hover:bg-card/90",
        compact ? "rounded-xl" : "rounded-2xl",
        dimmed && "bg-card/40 shadow-none hover:bg-card/60",
      )}
    >
      {accent ? (
        <span
          aria-hidden="true"
          className={cn("absolute inset-y-0 left-0 w-[3px]", ACCENT_BAR[accent])}
        />
      ) : null}
      {/* Collapsed summary row — the whole row toggles the detail pane
          (mirrors the Admin bookings list). Inner interactive elements (the
          trip-id copy button + the action icons) stop click propagation so a
          tap on them doesn't also expand/collapse the row; the keyboard
          toggle only fires when the row itself is the focused target. It
          sits above the accent bar and follows the card's corners, so its
          focus ring is drawn in full. */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`Trip ${friendlyId} for ${patientName} — ${
          expanded ? "hide" : "show"
        } details`}
        onClick={() => {
          setAnimateOpen(true);
          setExpanded((v) => !v);
        }}
        onKeyDown={(e) => {
          if (
            (e.key === "Enter" || e.key === " ") &&
            e.target === e.currentTarget
          ) {
            e.preventDefault();
            setAnimateOpen(true);
            setExpanded((v) => !v);
          }
        }}
        className="relative z-[1] flex w-full cursor-pointer flex-wrap items-center gap-x-3 gap-y-2.5 rounded-[inherit] px-4 py-3 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:flex-nowrap"
      >
        {/* Left zone — trip id + meta pills (trip type, transport, status),
            then patient · MRN. Same pill vocabulary as the admin Bookings
            list so the two surfaces read as one product. */}
        <div className="flex min-w-0 flex-1 basis-full flex-col gap-1.5 sm:basis-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 inline-flex items-center"
            >
              <CopyableTripId friendlyId={friendlyId} query={query} compact />
            </span>
            <MetaPill tone="primary" title={pairing ? pairingTitle : undefined}>
              {pairing ? (
                <>
                  <Repeat className="h-2.5 w-2.5" /> Round trip
                </>
              ) : (
                "One way"
              )}
            </MetaPill>
            {svc.label ? <MetaPill tone="slate">{svc.label}</MetaPill> : null}
            <span
              className={cn(
                "whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider",
                statusBadgeClasses(status),
              )}
            >
              {prettyStatus(status)}
            </span>
          </div>
          <div className="flex min-w-0 items-baseline gap-1.5 leading-tight">
            <span className={cn("truncate text-[15px] font-semibold", dimmed ? "text-foreground/80" : "text-foreground")}>
              {highlightMatch(patientName, query)}
            </span>
            {trip.mrn ? (
              <span className="shrink-0 font-mono text-[0.65rem] uppercase text-foreground/70">
                · MRN {highlightMatch(trip.mrn, query)}
              </span>
            ) : null}
          </div>
        </div>

        {/* Right zone — pickup time / live ETA, quick actions, expand
            chevron. Quick actions let dispatch act without expanding
            (Activate Will Call especially). */}
        <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto">
          {etaCountdown ? (
            <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-xs font-semibold text-green-700 dark:text-green-400">
              {etaCountdown}
            </span>
          ) : !isWillCall && trip.pickupTime ? (
            <span className={cn("font-mono text-[13px] font-medium tabular-nums", dimmed ? "text-foreground/70" : "text-foreground/80")}>
              {trip.pickupTime}
            </span>
          ) : null}

          {/* On a phone this zone takes its own line: time on the left,
              actions and the chevron pushed right. */}
          <div className="ml-auto flex items-center gap-2">
            {hasActions ? (
              <span
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5"
              >
                {isEditableNow(status, trip.pickupAtIso) ? (
                  <EditTrip
                    friendlyId={friendlyId}
                    patientName={patientName}
                    pickupAtIso={trip.pickupAtIso}
                    appointmentAtIso={trip.appointmentAtIso}
                    isWillCall={isWillCall}
                    currentPickupAddress={trip.pickupAddress}
                    currentDestinationAddress={trip.destinationAddress}
                    compact
                  />
                ) : null}
                {isWillCall ? (
                  <ActivateWillCall friendlyId={friendlyId} compact />
                ) : null}
                {CANCELLABLE_STATUSES.has(status) ? (
                  <CancelTrip friendlyId={friendlyId} patientName={patientName} compact />
                ) : null}
              </span>
            ) : null}

            <span
              aria-hidden="true"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground"
            >
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </span>
          </div>
        </div>
      </div>

      {expanded ? (
        // Open animates via grid-template-rows 0fr→1fr (auto-height friendly,
        // no JS measuring); the inner min-h-0/overflow-hidden wrapper clips and
        // fades the content in. Close just unmounts — same mechanic as the
        // Admin bookings list.
        <div className={cn("grid", animateOpen && "animate-expand-open")}>
          <div className={cn("min-h-0 overflow-hidden", animateOpen && "animate-details-in")}>
            <TripDetails
              trip={trip}
              status={status}
              isWillCall={isWillCall}
              pairing={pairing}
            />
          </div>
        </div>
      ) : null}
    </article>
  );
}

// =============================================================================
// TripDetails — the expanded pane: the full read-only trip detail (service,
// DOB, contact/callback phones, pickup/appt time, addresses, cost center,
// ETA, note). Mirrors the Admin bookings list's expand-to-detail treatment.
// =============================================================================

function TripDetails({
  trip,
  status,
  isWillCall,
  pairing,
}: {
  trip: TripScheduleItem;
  status: string;
  isWillCall: boolean;
  pairing?: TripPairing | null;
}) {
  const svc = formatServiceType(trip.serviceType);
  const showEtaLine = trip.eta && status === "en_route";

  return (
    <div className="border-t border-border/60 bg-foreground/[0.015] px-4 py-3.5 text-sm">
      {pairing ? (
        <p className="mb-2 flex flex-wrap items-center gap-1.5 text-[0.7rem] text-muted-foreground">
          <Repeat className="h-3 w-3 shrink-0" />
          <span className="font-semibold uppercase tracking-wider">Round trip</span>
          {pairing.siblings.map((s) => (
            <Fragment key={s.friendlyId}>
              <span className="text-muted-foreground/60">·</span>
              <span className="font-semibold text-foreground/80">{s.label}:</span>
              <span className="font-mono text-foreground/75">{s.friendlyId}</span>
              <span
                className={cn(
                  "rounded-full border px-1.5 py-px font-bold uppercase tracking-wider text-[0.6rem]",
                  statusBadgeClasses(s.status),
                )}
              >
                {prettyStatus(s.status)}
              </span>
            </Fragment>
          ))}
        </p>
      ) : null}

      {svc.label ? (
        <p className="mb-1.5 flex items-center gap-1.5 text-foreground/70">
          {svc.icon ? <ServiceIcon kind={svc.icon} /> : null}
          <span>{svc.label}</span>
        </p>
      ) : null}

      <dl className="grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2">
        {trip.dob ? <DetailRow label="DOB">{trip.dob}</DetailRow> : null}
        <PhoneDetail label="Contact" phones={trip.contactPhones} />
        <PhoneDetail
          label="Callback"
          phones={trip.callbackPhone ? [trip.callbackPhone] : undefined}
        />
        {!isWillCall && trip.pickupTime ? (
          <DetailRow label="PU">{trip.pickupTime}</DetailRow>
        ) : null}
        {trip.appointmentTime ? (
          <DetailRow label="Appt">{trip.appointmentTime}</DetailRow>
        ) : null}
        {trip.costCenter ? (
          <DetailRow label="Cost center">
            <span className="font-mono">{trip.costCenter}</span>
          </DetailRow>
        ) : null}
        {showEtaLine ? <DetailRow label="ETA">{trip.eta}</DetailRow> : null}
        {trip.pickupAddress ? (
          <DetailRow label="From" full>
            {trip.pickupAddress}
          </DetailRow>
        ) : null}
        {trip.destinationAddress ? (
          <DetailRow label="To" full>
            {trip.destinationAddress}
          </DetailRow>
        ) : null}
      </dl>

      <NoteBlock note={trip.note} />
    </div>
  );
}

/** One label/value pair in the detail grid. `full` spans both columns
 *  (addresses, which can be long). */
function DetailRow({
  label,
  full,
  children,
}: {
  label: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("flex gap-2", full ? "sm:col-span-2" : "")}>
      <dt className="w-24 shrink-0 pt-0.5 text-[0.68rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </dt>
      <dd className="min-w-0 break-words text-foreground/80">{children}</dd>
    </div>
  );
}

/** Phone row inside the detail grid — tap-to-call links pretty-printed to
 *  "(XXX) XXX-XXXX". Hidden when the list is empty so absence is invisible. */
function PhoneDetail({ label, phones }: { label: string; phones?: string[] }) {
  const items = (phones ?? []).map((d) => d.trim()).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <DetailRow label={label}>
      {items.map((digits, i) => {
        const display = formatUsPhone(digits) || digits;
        return (
          <span key={digits}>
            {i > 0 ? <span className="mx-1 text-muted-foreground">·</span> : null}
            <a
              href={`tel:${digits}`}
              className="font-mono underline-offset-2 hover:underline"
            >
              {display}
            </a>
          </span>
        );
      })}
    </DetailRow>
  );
}

// =============================================================================
// NoteBlock
//
// Booking note from triplink. Multi-line, can be long and structured
// ("Department: …", "Requested By: …", "Cost Center: …") or short and
// free-form. Truncated to 2 lines by default with a click-to-expand
// toggle so the pane stays scannable but the full text is one tap away.
// Hidden entirely when there is no note.
// =============================================================================

function NoteBlock({ note }: { note?: string }) {
  const [expanded, setExpanded] = useState(false);
  const trimmed = (note ?? "").trim();
  if (!trimmed) return null;

  return (
    <div className="mt-3 border-t border-border/60 pt-2.5">
      <span className="mr-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        Note:
      </span>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          "mt-0.5 block w-full text-left text-sm text-foreground/70 hover:text-foreground",
          expanded ? "whitespace-pre-wrap" : "line-clamp-2",
        )}
        aria-expanded={expanded}
        title={expanded ? "Click to collapse" : "Click to expand"}
      >
        {trimmed}
      </button>
    </div>
  );
}

// =============================================================================
// Activate Will Call
//
// Patient called in — flip the trip into the active queue. Hits the BFF
// proxy at /api/trip/activate-will-call. NOT_IMPLEMENTED is the
// defensive-fallback toast for the rare case where upstream is
// temporarily unavailable (the endpoint itself is wired live).
// =============================================================================

function ActivateWillCall({
  friendlyId,
  compact,
}: {
  friendlyId: string;
  compact?: boolean;
}) {
  const canActivate = useHasPermission("activateWillCall");
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const mutation = useMutation({
    mutationFn: () => api.trip.activateWillCall({ friendlyId }),
    onSuccess: () => {
      toast.success(`Trip ${friendlyId} activated`);
      void queryClient.invalidateQueries({ queryKey: ["schedule"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "NOT_IMPLEMENTED") {
        toast.warning("Couldn't reach dispatch for will-call activation. Try again in a moment.");
        return;
      }
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't activate the trip.",
      );
    },
  });

  // Light confirmation, not destructive — guards against an accidental
  // misclick on a busy dispatch board without slowing the workflow
  // down. Activation is reversible by Cancel, but it still triggers
  // real upstream dispatch state changes, so a one-tap "are you
  // sure" is worth the friction.
  async function onClick() {
    const proceed = await confirm({
      title: `Activate will-call trip ${friendlyId}?`,
      description:
        "Dispatch will be notified that the patient called in. The trip moves into the active queue and a vehicle is assigned.",
      confirmText: "Activate",
      cancelText: "Not yet",
    });
    if (!proceed) return;
    mutation.mutate();
  }

  // Permission gate (after all hooks). View-only users don't see the
  // button — and the server enforces it too.
  if (!canActivate) return null;

  // Compact rows show an icon-only button so the row stays one line tall.
  // The aria-label + title attribute keep the affordance discoverable for
  // keyboard / screen-reader users; the visual label appears in the
  // detailed view (success/error feedback now lives in a global toast).
  if (compact) {
    return (
      <button
        type="button"
        disabled={mutation.isPending}
        aria-label={`Activate will-call trip ${friendlyId}`}
        title="Patient called in — activate this trip now."
        onClick={() => void onClick()}
        className="relative shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-primary/50 bg-primary/15 text-primary transition before:absolute before:-inset-1.5 before:content-[''] hover:border-primary hover:bg-primary/25 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {mutation.isPending ? <Spinner /> : <PhoneCall className="h-3.5 w-3.5" />}
      </button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="primary"
      disabled={mutation.isPending}
      title="Patient called in — activate this trip now."
      onClick={() => void onClick()}
      className="min-w-[7rem]"
    >
      {mutation.isPending ? (
        <>
          <Spinner /> Activating…
        </>
      ) : (
        <>
          <PhoneCall className="h-3.5 w-3.5" /> Activate
        </>
      )}
    </Button>
  );
}

// =============================================================================
// Cancel Trip
//
// Available on `pending` / `assigned` / `will_call` trips — the button
// is hidden for in-flight or terminal statuses by the parent. Wired
// to triplink's /cancel endpoint with the same defensive
// NOT_IMPLEMENTED fallback as activate-will-call. While DRY_RUN is on
// the BFF returns a mock success so QA can exercise the UI flow
// without dispatching real changes.
// =============================================================================

function CancelTrip({
  friendlyId,
  patientName,
  compact,
}: {
  friendlyId: string;
  patientName: string;
  compact?: boolean;
}) {
  const canCancel = useHasPermission("cancelTrip");
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);

  const mutation = useMutation({
    // Reason flows in from the dialog, lands in the audit log
    // (`metadata.reason` on the trip.cancel entry), and forwards to
    // triplink the moment they wire up the cancel endpoint.
    mutationFn: (reason: string) => api.trip.cancel({ friendlyId, reason }),
    onSuccess: () => {
      setDialogOpen(false);
      toast.success(`Trip ${friendlyId} cancelled`);
      void queryClient.invalidateQueries({ queryKey: ["schedule"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "NOT_IMPLEMENTED") {
        // Defensive path — upstream is normally wired. If we land
        // here it's a temporary outage; the audit log entry still
        // landed (with the captured reason), so close the dialog
        // and surface a friendly toast.
        setDialogOpen(false);
        toast.warning("Couldn't reach dispatch to cancel the trip. Try again in a moment.");
        return;
      }
      // Dispatch refused for where the trip is now (on its way, finished):
      // retrying can't succeed, so close the dialog, as for a refused change,
      // and refetch so the card shows the trip's status.
      if (err instanceof ApiError && err.code === "CANCEL_REFUSED") {
        setDialogOpen(false);
        toast.warning(err.message, { duration: 8000 });
        void queryClient.invalidateQueries({ queryKey: ["schedule"] });
        return;
      }
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't cancel the trip.",
      );
    },
  });

  if (!canCancel) return null;

  const button = compact ? (
    <button
      type="button"
      disabled={mutation.isPending}
      aria-label={`Cancel trip ${friendlyId}`}
      title="Cancel this trip"
      onClick={() => setDialogOpen(true)}
      className="relative shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border/80 bg-foreground/[0.03] text-rose-600 dark:text-rose-300 transition before:absolute before:-inset-1.5 before:content-[''] hover:border-rose-400/60 hover:bg-rose-400/10 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {mutation.isPending ? <Spinner /> : <XCircle className="h-3.5 w-3.5" />}
    </button>
  ) : (
    <Button
      type="button"
      size="sm"
      variant="danger"
      disabled={mutation.isPending}
      title="Cancel this trip"
      onClick={() => setDialogOpen(true)}
      className="min-w-[7rem]"
    >
      {mutation.isPending ? (
        <>
          <Spinner /> Cancelling…
        </>
      ) : (
        <>
          <XCircle className="h-3.5 w-3.5" /> Cancel
        </>
      )}
    </Button>
  );

  return (
    <>
      {button}
      <CancelTripDialog
        open={dialogOpen}
        friendlyId={friendlyId}
        patientName={patientName}
        pending={mutation.isPending}
        onCancel={() => setDialogOpen(false)}
        onConfirm={(reason) => mutation.mutate(reason)}
      />
    </>
  );
}

// =============================================================================
// Edit Trip
//
// Available on `pending` / `assigned` / `will_call` trips whose pickup is
// still beyond the edit lead time (see EDITABLE_STATUSES + isEditableNow). Opens
// EditTripDialog; the dialog gathers the optional time + address fields
// and submits via api.trip.update. The backend does NOT re-validate the
// lead time (see isEditableNow above), so a stale render CAN submit a late
// edit — the dialog's own save gate is the second and last line of defence.
// =============================================================================

function EditTrip({
  friendlyId,
  patientName,
  pickupAtIso,
  appointmentAtIso,
  isWillCall,
  currentPickupAddress,
  currentDestinationAddress,
  compact,
}: {
  friendlyId: string;
  patientName: string;
  pickupAtIso?: string;
  appointmentAtIso?: string;
  /** Will-call trips don't have a fixed pickup time, so the edit
   *  dialog hides the time row and only lets dispatch update the
   *  pickup / destination addresses. */
  isWillCall: boolean;
  /** Current pickup / destination, so the dialog can warn when an edit would
   *  leave the two the same address. The live feed carries them as single
   *  combined strings (no separate city/zip). */
  currentPickupAddress?: string;
  currentDestinationAddress?: string;
  compact?: boolean;
}) {
  const canModify = useHasPermission("modifyTrip");
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload: EditTripPayload) =>
      api.trip.update({ friendlyId, ...payload }),
    onSuccess: () => {
      setDialogOpen(false);
      toast.success(`Trip ${friendlyId} updated`);
      void queryClient.invalidateQueries({ queryKey: ["schedule"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "NOT_IMPLEMENTED") {
        setDialogOpen(false);
        toast.warning("Couldn't reach dispatch to save the edit. Try again in a moment.");
        return;
      }
      // Dispatch refused the edit because the trip is too close to pickup.
      // Retrying can NEVER succeed, so close the dialog rather than leaving
      // the booker staring at a filled-in form they will resubmit — the exact
      // loop the unroutable-address fix was written to stop (that booker
      // retried five times in thirty seconds). Warning, not error: nothing is
      // broken, and the message says to call dispatch instead.
      if (err instanceof ApiError && err.code === "EDIT_WINDOW_CLOSED") {
        setDialogOpen(false);
        toast.warning(err.message, { duration: 8000 });
        return;
      }
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't update the trip.",
      );
    },
  });

  if (!canModify) return null;

  const button = compact ? (
    <button
      type="button"
      disabled={mutation.isPending}
      aria-label={`Edit trip ${friendlyId}`}
      title="Edit pickup time or addresses"
      onClick={() => setDialogOpen(true)}
      className="relative shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border/80 bg-foreground/[0.03] text-blue-600 transition before:absolute before:-inset-1.5 before:content-[''] hover:border-blue-400/60 hover:bg-blue-400/10 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-blue-300"
    >
      {mutation.isPending ? <Spinner /> : <Pencil className="h-3.5 w-3.5" />}
    </button>
  ) : (
    // Pill-styled to match Activate / Cancel siblings — earlier this
    // was variant="ghost" which made Edit look orphaned next to two
    // bordered buttons in the will-call stack. Sky-blue tint matches
    // the compact icon-only Edit button so the action keeps a
    // consistent visual identity across views, while staying distinct
    // from Activate's primary gradient and Cancel's rose pill.
    <Button
      type="button"
      size="sm"
      variant="secondary"
      disabled={mutation.isPending}
      title="Edit pickup time or addresses"
      onClick={() => setDialogOpen(true)}
      className="min-w-[7rem] border-blue-500/45 bg-blue-400/10 text-blue-700 hover:border-blue-500 hover:bg-blue-400/20 dark:border-blue-400/45 dark:text-blue-300 dark:hover:border-blue-400"
    >
      {mutation.isPending ? (
        <>
          <Spinner /> Saving…
        </>
      ) : (
        <>
          <Pencil className="h-3.5 w-3.5" /> Edit
        </>
      )}
    </Button>
  );

  return (
    <>
      {button}
      <EditTripDialog
        open={dialogOpen}
        friendlyId={friendlyId}
        patientName={patientName}
        pickupAtIso={pickupAtIso}
        appointmentAtIso={appointmentAtIso}
        isWillCall={isWillCall}
        currentPickupAddress={currentPickupAddress}
        currentDestinationAddress={currentDestinationAddress}
        pending={mutation.isPending}
        onCancel={() => setDialogOpen(false)}
        onConfirm={(payload) => mutation.mutate(payload)}
      />
    </>
  );
}

// =============================================================================
// MetaPill — small uppercase pill for trip-type / transport metadata on the
// collapsed card. Mirrors the admin Bookings list's Pill so both trip lists
// share one visual vocabulary.
// =============================================================================

function MetaPill({
  tone,
  title,
  children,
}: {
  tone: "primary" | "slate";
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider",
        tone === "primary"
          ? "border-primary/45 bg-primary/10 text-blue-700 dark:text-primary"
          : "border-foreground/15 bg-foreground/[0.04] text-foreground/80",
      )}
    >
      {children}
    </span>
  );
}

// =============================================================================
// CopyableTripId
//
// Click-to-copy wrapper around the friendlyId display. The trip ID is
// the thing dispatch reads aloud to drivers / pastes into call logs,
// so making it one-click-copyable saves a "select-the-text-with-mouse"
// dance every shift. Copy icon stays low-key (60% opacity on hover);
// the button itself has a hover affordance via background.
// =============================================================================

function CopyableTripId({
  friendlyId,
  query,
  compact,
}: {
  friendlyId: string;
  query: string;
  compact?: boolean;
}) {
  async function copy() {
    try {
      await navigator.clipboard.writeText(friendlyId);
      toast.success(`Copied ${friendlyId}`);
    } catch {
      // Older browsers / non-HTTPS contexts where clipboard API is
      // unavailable. Fall back to letting the user know.
      toast.error("Couldn't copy. Select the trip ID and use Cmd/Ctrl+C.");
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy trip ID ${friendlyId}`}
      aria-label={`Copy trip ID ${friendlyId}`}
      className={cn(
        "group inline-flex items-center gap-1 rounded-md font-mono tabular-nums transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        compact
          ? "text-[0.7rem] font-semibold text-foreground/85 hover:text-foreground"
          : "px-1 -mx-1 text-sm text-foreground hover:bg-foreground/[0.06]",
      )}
    >
      <span>{highlightMatch(friendlyId, query)}</span>
      <Copy
        className={cn(
          "shrink-0 opacity-0 transition group-hover:opacity-60 group-focus-visible:opacity-60",
          compact ? "h-2.5 w-2.5" : "h-3 w-3",
        )}
      />
    </button>
  );
}

function formatEtaCountdown(eta: string | undefined): string {
  if (!eta) return "";
  const ms = Date.parse(eta);
  if (Number.isNaN(ms)) return "";
  const diff = Math.round((ms - Date.now()) / 60000);
  if (diff <= 0) return "Arriving";
  if (diff === 1) return "in 1 min";
  if (diff < 60) return `in ${diff} min`;
  const hours = Math.floor(diff / 60);
  const mins = diff % 60;
  return mins ? `in ${hours}h ${mins}m` : `in ${hours}h`;
}
